import { Injectable } from '@angular/core';

import { filter, map, Observable, Subject } from 'rxjs';

import { KrosModalRef } from '@kros-sk/ui/modal';

/**
 * The bridge between whoever opens a modal and whoever closes it from outside the element tree.
 *
 * A `TemplateRef` rendered by `KrosModalComponent` resolves DI through its declaration injector, so
 * nothing inside the template can inject `KrosModalRef`. A survey completion handler is a route-level
 * provider and a schema action runs in the `EnvironmentInjector`, so neither sees the element injector
 * of the modal either. A root service keyed by the modal's handle is what all of them can reach.
 */
@Injectable({ providedIn: 'root' })
export class SchemaModalRefService {
  readonly #refs = new Map<string, KrosModalRef>();
  readonly #closed = new Subject<string>();

  /** Emits the `modalId` of every modal that closed. Not replayed: a late reader has fresh data already. */
  readonly closed$: Observable<string> = this.#closed.asObservable();

  /** Wires the close itself, so an opener has nothing to clear or announce. */
  register(modalId: string, modalRef: KrosModalRef): void {
    this.#refs.set(modalId, modalRef);

    modalRef.afterClosed$.subscribe(() => {
      if (this.#refs.get(modalId) === modalRef) {
        this.#refs.delete(modalId);
      }

      this.#closed.next(modalId);
    });
  }

  submit(modalId: string): void {
    this.#refs.get(modalId)?.submit();
  }

  cancel(modalId: string): void {
    this.#refs.get(modalId)?.cancel();
  }

  closedFor(modalId: string): Observable<void> {
    return this.closed$.pipe(
      filter((closedId) => closedId === modalId),
      map(() => undefined)
    );
  }
}
